import { dataResidencyManager, SupportedDataResidency } from './DataResidencyManager';

export const COGNITO_RETRY_MAX_RETRIES = 10;
export const COGNITO_RETRY_BASE_DELAY_MS = 250;
export const COGNITO_RETRY_MAX_DELAY_MS = 30000;
const DEFAULT_JITTER_MAX_MS = 250;
const RETRYABLE_COGNITO_ERROR_CODES = new Set(['InternalError', 'InternalErrorException']);

type SleepFunction = (delayMs: number) => Promise<void>;
type JitterFunction = () => number;

export interface CognitoRetryOptions {
  dataResidency?: SupportedDataResidency;
  sleep?: SleepFunction;
  getJitterMs?: JitterFunction;
}

export function getCognitoRetryDelayMs(retryIndex: number, jitterMs = 0): number {
  const exponentialDelay = COGNITO_RETRY_BASE_DELAY_MS * 2 ** retryIndex;
  return Math.min(exponentialDelay + jitterMs, COGNITO_RETRY_MAX_DELAY_MS);
}

export function getCognitoErrorHttpStatusCode(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  if ('statusCode' in error && typeof error.statusCode === 'number') {
    return error.statusCode;
  }

  if (
    '$metadata' in error &&
    error.$metadata &&
    typeof error.$metadata === 'object' &&
    'httpStatusCode' in error.$metadata &&
    typeof error.$metadata.httpStatusCode === 'number'
  ) {
    return error.$metadata.httpStatusCode;
  }

  if (
    'response' in error &&
    error.response &&
    typeof error.response === 'object' &&
    'status' in error.response &&
    typeof error.response.status === 'number'
  ) {
    return error.response.status;
  }

  if ('originalError' in error) {
    return getCognitoErrorHttpStatusCode(error.originalError);
  }

  return undefined;
}

export function getCognitoErrorCode(error: unknown): string | undefined {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  if ('code' in error && typeof error.code === 'string') {
    return error.code;
  }

  if ('name' in error && typeof error.name === 'string') {
    return error.name;
  }

  if ('__type' in error && typeof error.__type === 'string') {
    return error.__type.split('#').pop();
  }

  if ('originalError' in error) {
    return getCognitoErrorCode(error.originalError);
  }

  return undefined;
}

function getDefaultJitterMs(): number {
  return Math.floor(Math.random() * DEFAULT_JITTER_MAX_MS);
}

function sleep(delayMs: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, delayMs);
  });
}

function shouldRetryInMeCentral1(dataResidency: SupportedDataResidency, error: unknown): boolean {
  if (dataResidency !== SupportedDataResidency.UAE) {
    return false;
  }

  const statusCode = getCognitoErrorHttpStatusCode(error);
  if (statusCode === 500) {
    return true;
  }

  const errorCode = getCognitoErrorCode(error);
  return errorCode !== undefined && RETRYABLE_COGNITO_ERROR_CODES.has(errorCode);
}

export async function retryCognitoInMeCentral1<T>(
  operationName: string,
  operation: () => Promise<T>,
  options: CognitoRetryOptions = {},
): Promise<T> {
  const dataResidency = options.dataResidency ?? dataResidencyManager.getCurrentDataResidency();
  const sleepFunction = options.sleep ?? sleep;
  const getJitterMs = options.getJitterMs ?? getDefaultJitterMs;

  let retryCount = 0;

  while (true) {
    try {
      return await operation();
    } catch (error) {
      if (!shouldRetryInMeCentral1(dataResidency, error) || retryCount >= COGNITO_RETRY_MAX_RETRIES) {
        throw error;
      }

      const delayMs = getCognitoRetryDelayMs(retryCount, getJitterMs());
      const statusCode = getCognitoErrorHttpStatusCode(error);
      const errorCode = getCognitoErrorCode(error);

      retryCount += 1;
      console.warn(
        `[CognitoRetry] ${operationName} failed with status ${statusCode ?? 'unknown'} ` +
          `and code ${errorCode ?? 'unknown'} in me-central-1, ` +
          `retry ${retryCount}/${COGNITO_RETRY_MAX_RETRIES} after ${delayMs}ms`,
      );

      await sleepFunction(delayMs);
    }
  }
}
